import { useMemo } from 'react';
import { selector, useRecoilState, useRecoilValue } from 'recoil';
import {
  dashboardsCollectionState,
  dashboardsLoadedState,
  dashboardsState,
  externalDashboardQueriesState,
  externalDashboardsState,
  groupDashboardQueriesState,
  isPublicViewState,
  isSharedState
} from '../../store/dashboardsAtoms';
import { splitter } from '../../helpers/textSplitter';

const mappedOverviewQueriesSelector = selector({
  key: 'mappedOverviewQueriesSelector',
  get: ({ get }) => {
    const groupDashboardQueries = get(groupDashboardQueriesState);
    const externalDashboardQueries = get(externalDashboardQueriesState);

    const allQueries = [...groupDashboardQueries, ...externalDashboardQueries];
    const mappedQueries = new Map<string, any>();

    allQueries.forEach(query => {
      const key = `${query.dashboardId}:${query.title}`;

      if (!mappedQueries.has(key)) {
        const answer: any = JSON.parse(query?.answer || '{}')?.answer ?? '';

        if (query.title === 'Digest')
          mappedQueries.set(key, splitter(answer as string).map(split => split.sent)?.[0]);
        else
          mappedQueries.set(key, answer);
      }
    });

    return mappedQueries;
  },
});

const useDashboardsContext = () => {
  const [dashboardsLoaded, setDashboardsLoaded] = useRecoilState(dashboardsLoadedState);
  const [dashboards, setDashboards] = useRecoilState(dashboardsState);
  const [externalDashboards, setExternalDashboards] = useRecoilState(externalDashboardsState);
  const [dashboardsCollection, setDashboardsCollection] = useRecoilState(dashboardsCollectionState);
  const [groupDashboardQueries, setDashboardsQueries] = useRecoilState(groupDashboardQueriesState);
  const [externalDashboardQueries, setExternalDashboardQueries] = useRecoilState(externalDashboardQueriesState);
  const [isPublicView, setIsPublicView] = useRecoilState(isPublicViewState);
  const [isShared, setIsShared] = useRecoilState(isSharedState);
  const mappedOverviewQueries = useRecoilValue(mappedOverviewQueriesSelector);

  const context = useMemo(() => ({
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsCollection,
    dashboardsQueries: groupDashboardQueries,
    externalDashboardQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsCollection,
    setDashboardsQueries,
    setExternalDashboardQueries,
    setIsPublicView,
    setIsShared,
  }), [
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsCollection,
    groupDashboardQueries,
    externalDashboardQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsCollection,
    setDashboardsQueries,
    setExternalDashboardQueries,
    setIsPublicView,
    setIsShared,
  ]);

  return context;
};

export default useDashboardsContext;